import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useProximosVencimientos } from '@/core/hooks/useCalendario'
import { usePermissions } from '@/hooks/usePermissions'
import { filtrarVencimientosPorResponsable, indexarReparto } from '@/lib/vencimientos-utils'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useMapaResponsables, MAPA_VACIO } from '@/core/hooks/useResponsables'
import { useConceptosOrganizacion } from '@/core/hooks/useConceptos'
import { filtrarVencimientosPanel, etiquetaVencimiento } from './panel-utils'
import { formatearCUIT } from '@/lib/utils'

const DIAS_HORIZONTE = 7
const MAX_VISIBLES = 8

export function ProximosVencimientosCard() {
  const { data: vencimientos = [], isLoading } = useProximosVencimientos(DIAS_HORIZONTE)
  const { esAdmin } = usePermissions()
  const { usuario } = useOrganizacion()
  const { data: mapaResponsables, isError: mapaFallo, refetch: reintentarMapa } = useMapaResponsables()
  const { data: asignaciones, isError: repartoFallo, refetch: reintentarReparto } = useConceptosOrganizacion()
  const usuarioId = usuario?.id ?? null

  // El scoping necesita el mapa de responsables y el reparto. Sin alguno de los dos no se puede
  // decidir de quién es cada vencimiento, y el filtro no deja ninguno. El widget muestra la carga
  // mientras llegan y un aviso de error si alguno falló. Nunca muestra los vencimientos sin filtrar.
  const scopingIncompleto =
    !esAdmin && !!usuarioId && (mapaResponsables === undefined || asignaciones === undefined)
  const scopingFallido = scopingIncompleto && (mapaFallo || repartoFallo)
  const cargando = isLoading || (scopingIncompleto && !scopingFallido)

  const reintentarScoping = () => {
    if (mapaFallo) void reintentarMapa()
    if (repartoFallo) void reintentarReparto()
  }

  // ORR-205: el widget de Inicio se scopea por responsable (antes traía todos).
  // ORR-230 fase 4: por `usuario_id` y con el reparto aplicado, igual que la planilla.
  const pendientes = useMemo(
    () =>
      filtrarVencimientosPanel(
        filtrarVencimientosPorResponsable(vencimientos, {
          esAdmin,
          usuarioId,
          mapaResponsables: mapaResponsables ?? MAPA_VACIO,
          reparto: indexarReparto(asignaciones ?? []),
        }),
      ),
    [vencimientos, esAdmin, usuarioId, mapaResponsables, asignaciones],
  )

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Próximos vencimientos</CardTitle>
        <Link to="/calendario" className="text-sm text-muted-foreground hover:text-foreground">
          Ver calendario
        </Link>
      </CardHeader>
      <CardContent>
        {cargando ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-1">
                <div className="h-4 w-48 bg-muted animate-pulse rounded" />
                <div className="h-3 w-32 bg-muted animate-pulse rounded" />
              </div>
            ))}
          </div>
        ) : scopingFallido ? (
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <p className="text-sm text-destructive">
              No se pudo cargar a quién le corresponde cada vencimiento.
            </p>
            <Button variant="outline" size="sm" onClick={reintentarScoping}>
              Reintentar
            </Button>
          </div>
        ) : pendientes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Sin vencimientos pendientes en los próximos {DIAS_HORIZONTE} días
          </p>
        ) : (
          <div className="space-y-2">
            {pendientes.slice(0, MAX_VISIBLES).map((vencimiento) => {
              const etiqueta = etiquetaVencimiento(vencimiento)
              return (
                <div
                  key={vencimiento.id ?? `${vencimiento.cliente_id}-${vencimiento.fecha_vencimiento}`}
                  className="flex items-center justify-between gap-2"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">
                      {vencimiento.cliente_nombre ?? 'Sin cliente'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {vencimiento.cliente_cuit ? `${formatearCUIT(vencimiento.cliente_cuit)} · ` : ''}
                      {vencimiento.impuesto_nombre_corto ?? vencimiento.tipo_impuesto ?? ''}
                    </p>
                  </div>
                  <span
                    className={
                      etiqueta === 'Hoy'
                        ? 'text-xs font-medium text-red-600 shrink-0'
                        : 'text-xs text-muted-foreground shrink-0'
                    }
                  >
                    {etiqueta}
                  </span>
                </div>
              )
            })}
            {pendientes.length > MAX_VISIBLES && (
              <p className="text-xs text-muted-foreground">+{pendientes.length - MAX_VISIBLES} más</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
